import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { AppShell } from './shell/AppShell'
import { RequireSession } from './auth/RequireSession'
import { AuthCallback } from './auth/AuthCallback'
import { Boot } from './screens/Boot'
import { TenantLanding } from './screens/TenantLanding'
import { SiteEditor } from './screens/SiteEditor'
import { Enquiries } from './screens/site/Enquiries'
import { Enquiry } from './screens/site/Enquiry'
import { PageEditor } from './screens/PageEditor'
import { Library } from './screens/Library'
import { ComponentEditor } from './screens/ComponentEditor'
import { Overview } from './screens/Overview'
import { ContentSite } from './screens/content/ContentSite'
import { ContentPage } from './screens/content/ContentPage'
import { ContentRecords } from './screens/content/ContentRecords'
import { ContentRecord } from './screens/content/ContentRecord'
import { NotFound } from './screens/NotFound'
import { Integrations } from './screens/Integrations'
import { OperatorBilling } from './screens/operator/OperatorBilling'
import { OperatorConnections } from './screens/operator/OperatorConnections'
import { OperatorPlans } from './screens/operator/OperatorPlans'
import { OperatorTenant } from './screens/operator/OperatorTenant'
import { OperatorTenants } from './screens/operator/OperatorTenants'
import { Account } from './screens/Account'
import { ACCOUNT, DESIGN, HELP, OPERATOR, routes } from './model/routes'
import { Loading } from './ui/QueryState'

// **The guide is the one screen loaded on demand.** It carries a Markdown
// renderer and a picture for most steps of it, none of which somebody
// opening the admin to fix a typo needs to download first; every other
// screen is the work itself and is worth having in hand.
const Help = lazy(() => import('./screens/Help').then((module) => ({ default: module.Help })))
const help = (
  <Suspense fallback={<Loading label="Opening the guide…" />}>
    <Help />
  </Suspense>
)

// Routes mirror the model's addressing: one workspace, navigated by
// object and by task.
//
// The **tenant leads** because the API addresses a component as
// `(tenant, id)`, and because a URL an author pastes to a colleague
// should say which tenant it belongs to rather than hiding it inside an
// id. It is a route parameter and never a store value: the moment "the
// current tenant" lives in the store, two tabs open on two tenants start
// fighting over one value.
//
// **Under the tenant, a site or `design`.** Everything that is a fact
// about one site — its pages, its records, its routing, its releases —
// is under the site; everything that belongs to the tenant and is used
// by every site — the types, the patterns, the components, the webhooks
// — is under `design`. `design` is a static segment, so it outranks
// `/:tenant/:site` and is reserved as a site id, which is the same one
// name `build` reserved and for the same reason.
//
// **The mode is gone**, and `model/routes` argues why at length. What it
// leaves here is that every screen has exactly one address: a page's
// blocks and a page's structure are two faces of one page rather than
// one page in two registers, and nobody can paste a link that opens a
// different screen for the reader than it did for the sender.
//
// `/auth/callback` sits above the tenant routes and wins over `/:tenant`
// because React Router ranks a static segment above a dynamic one. It is
// the path registered with the pool, so it is not free to move.
export function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        element={
          <RequireSession>
            <AppShell />
          </RequireSession>
        }
      >
        <Route path="/" element={<Boot />} />

        {/* The reader's own account. Not a tenant's — an identity holds
            several memberships or none — so a static first segment
            beside the operator area, reserved as a tenant id for the
            same reason. Inside the shell, so the sidebar stays. */}
        <Route path={`/${ACCOUNT}`} element={<Account />} />

        {/* The operator area: registering, billing and offboarding
            tenants. A static first segment, so it outranks `/:tenant`,
            and `operator` is reserved as a tenant id for that reason.
            The tenant under it is `:tenantId`, because `:tenant` is what
            the shell reads to load a tenant's content, and an operator
            reading a customer's account is not inside that content. */}
        <Route path={`/${OPERATOR}`} element={<OperatorTenants />} />
        <Route path={`/${OPERATOR}/tenants/:tenantId`} element={<OperatorTenant />} />
        <Route path={`/${OPERATOR}/tenants/:tenantId/billing`} element={<OperatorTenant face="billing" />} />
        <Route path={`/${OPERATOR}/tenants/:tenantId/administrators`} element={<OperatorTenant face="administrators" />} />
        <Route path={`/${OPERATOR}/tenants/:tenantId/domains`} element={<OperatorTenant face="domains" />} />
        <Route path={`/${OPERATOR}/tenants/:tenantId/traffic`} element={<OperatorTenant face="traffic" />} />
        <Route path={`/${OPERATOR}/billing`} element={<OperatorBilling />} />
        <Route path={`/${OPERATOR}/plans`} element={<OperatorPlans />} />
        <Route path={`/${OPERATOR}/connections`} element={<OperatorConnections />} />

        {/* The tenant: its sites, or straight into the only one. */}
        <Route path="/:tenant" element={<TenantLanding />} />

        {/* The tenant's design objects. Above the site routes in this
            file only for reading order — React Router ranks a static
            segment above `/:tenant/:site` regardless of registration
            order, which is what makes `design` the one reserved site
            id.

            **One component library, with the type as a filter on it.**
            There were fifteen of these, one per registered type,
            generated from the manifest — the data model used as an
            information architecture. The filter is still built from
            `manifest.types`, so a newly registered type appears in it
            with no client change; what is gone is the claim that the
            registry's shape is the reader's. Types and Patterns keep
            their own addresses because they are the two things an
            agency designs, rather than things it makes. */}
        <Route path={`/:tenant/${DESIGN}/types`} element={<Library library="types" />} />
        <Route path={`/:tenant/${DESIGN}/types/:type`} element={<Library face="type" />} />
        <Route path={`/:tenant/${DESIGN}/patterns`} element={<Library library="patterns" />} />
        <Route path={`/:tenant/${DESIGN}/components`} element={<Library />} />
        <Route path={`/:tenant/${DESIGN}/components/:id`} element={<ComponentEditor />} />
        <Route path={`/:tenant/${DESIGN}/components/:id/fields`} element={<ComponentEditor face="fields" />} />
        <Route path={`/:tenant/${DESIGN}/components/:id/held-by`} element={<ComponentEditor face="held" />} />
        <Route path={`/:tenant/${DESIGN}/integrations`} element={<Integrations />} />

        {/* A site. The bare address is the Overview — what is waiting to
            go live, and what is quietly wrong — and the pages are one
            segment down.

            A page has four faces and each is a path, because the path is
            where the reader is and the query is what they have selected.
            The bare page is the blocks, which is what the commoner
            reader came for; `structure` is the same page with the model
            showing; `preview` is the page as a visitor gets it, which
            needs the whole screen to be truthful about a site's own
            breakpoints; `settings` is the page's own facts. */}
        <Route path="/:tenant/:site" element={<Overview />} />
        <Route path="/:tenant/:site/pages" element={<ContentSite />} />
        <Route path="/:tenant/:site/pages/:pageId" element={<ContentPage />} />
        <Route path="/:tenant/:site/pages/:pageId/structure" element={<PageEditor />} />
        <Route path="/:tenant/:site/pages/:pageId/preview" element={<ContentPage face="preview" />} />
        <Route path="/:tenant/:site/pages/:pageId/settings" element={<ContentPage face="settings" />} />
        {/* A listed record type, and one record, as the site's owner
            edits them: "the albums", and one album with its photos. */}
        <Route path="/:tenant/:site/records/:type" element={<ContentRecords />} />
        <Route path="/:tenant/:site/records/:type/:id" element={<ContentRecord />} />
        <Route path="/:tenant/:site/records/:type/:id/settings" element={<ContentRecord face="settings" />} />
        <Route path="/:tenant/:site/records/:type/:id/preview" element={<ContentRecord face="preview" />} />
        {/* The site's own four faces. Routing keeps its own address
            beside the pages table: the route dialog and the
            generated-route dialog are here and nowhere else. */}
        {/* What visitors sent, which is a fact about the site rather
            than something its owner authored — so it is its own screen
            under the site and not a face of the Website document. */}
        <Route path="/:tenant/:site/enquiries" element={<Enquiries />} />
        <Route path="/:tenant/:site/enquiries/:id" element={<Enquiry />} />
        <Route path="/:tenant/:site/routing" element={<SiteEditor />} />
        <Route path="/:tenant/:site/composition" element={<SiteEditor face="compose" />} />
        <Route path="/:tenant/:site/settings" element={<SiteEditor face="settings" />} />
        <Route path="/:tenant/:site/releases" element={<SiteEditor face="releases" />} />
        {/* The guide, read beside the site. Its words are the same for
            every tenant, but the menu around them is not: at an address
            that named no site the shell dropped the Site and Content
            groups the guide tells the reader to use. Under the site it
            draws exactly as on every other site screen. `help` is a
            segment after the site, beside `pages` and `releases`, so it
            reserves nothing — no tenant id and no site id. */}
        <Route path={`/:tenant/:site/${HELP}`} element={help} />
        <Route path={`/:tenant/:site/${HELP}/:chapter`} element={help} />

        {/* Where the old addresses go.
            **Nothing that was addressable stops resolving.** Every build
            mode path had a reader who bookmarked it or pasted it, and a
            reshaping of the information architecture is not a reason to
            hand them a 404 — a redirect is cheap and it is the only
            honest way to move something. They are `replace`, so a reader
            who arrives on one and hits Back leaves rather than bouncing
            off the redirect into it again. */}
        <Route path="/:tenant/:site/overview" element={<Moved to={(p) => routes.site(p.tenant!, p.site!)} />} />
        <Route path="/:tenant/build" element={<Moved to={(p) => routes.tenant(p.tenant!)} />} />
        <Route path="/:tenant/build/library/:type" element={<Moved to={(p) => routes.components(p.tenant!, p.type!)} />} />
        <Route path="/:tenant/build/library/:type/type" element={<Moved to={(p) => routes.type(p.tenant!, p.type!)} />} />
        <Route path="/:tenant/build/components/:id" element={<Moved to={(p) => routes.component(p.tenant!, p.id!)} />} />
        <Route
          path="/:tenant/build/components/:id/fields"
          element={<Moved to={(p) => routes.componentFields(p.tenant!, p.id!)} />}
        />
        <Route
          path="/:tenant/build/components/:id/held-by"
          element={<Moved to={(p) => routes.componentHeldBy(p.tenant!, p.id!)} />}
        />
        <Route path="/:tenant/build/integrations" element={<Moved to={(p) => routes.integrations(p.tenant!)} />} />
        <Route path="/:tenant/build/:site" element={<Moved to={(p) => routes.siteRouting(p.tenant!, p.site!)} />} />
        <Route
          path="/:tenant/build/:site/composition"
          element={<Moved to={(p) => routes.siteComposition(p.tenant!, p.site!)} />}
        />
        <Route
          path="/:tenant/build/:site/settings"
          element={<Moved to={(p) => routes.siteSettings(p.tenant!, p.site!)} />}
        />
        <Route
          path="/:tenant/build/:site/releases"
          element={<Moved to={(p) => routes.siteReleases(p.tenant!, p.site!)} />}
        />
        <Route
          path="/:tenant/build/:site/pages/:pageId"
          element={<Moved to={(p) => routes.pageStructure(p.tenant!, p.site!, p.pageId!)} />}
        />
        <Route
          path="/:tenant/build/:site/pages/:pageId/preview"
          element={<Moved to={(p) => routes.pagePreview(p.tenant!, p.site!, p.pageId!)} />}
        />

        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

/**
 * An address that moved, redirecting to where it went.
 *
 * `<Navigate>` takes a path and not a pattern, so the new address is
 * built from the matched parameters rather than written out with `:id`
 * in it — which also means it goes through `routes`, and a later change
 * to the new shape carries the old addresses with it instead of leaving
 * them pointing at something that no longer exists.
 */
function Moved({ to }: { to: (params: Readonly<Record<string, string | undefined>>) => string }) {
  const params = useParams()
  return <Navigate to={to(params)} replace />
}
